
import type { SetupContext } from 'vue'
import { computed, defineComponent, onMounted, ref, watch } from 'vue'

import { ColorEnum } from '../../../enums'
import { EpbBaseInput } from '../epb-base-input'
import { EpbInputLabel } from '../epb-input-label'

import { EpbText } from '../epb-text'

export default defineComponent({
  name: 'EpbSlide',
  components: {
    EpbBaseInput,
    EpbInputLabel,
    EpbText,
  },
  props: {
    value: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 10,
    },
    help: {
      type: String,
      default: undefined,
    },
    helpIcon: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    icon: {
      type: String,
      default: undefined,
    },
    badgeSize: {
      type: String, // sm, md
      default: 'md',
    },
    disabled: Boolean,
    lowLight: Boolean,
    color: {
      type: String,
      default: ColorEnum.NEUTRAL_25,
    },
  },
  emits: {
    input: (value: number) => {},
  },
  setup(props, { emit }: SetupContext) {
    const baseClass = 'epb-slide'

    const internalValue = ref(props.value)
    const thumbSize = ref(12) // pixels
    const markerVisible = ref(false)

    const inputValue = (event: Event): void => {
      const newValue = Number((event.target as HTMLInputElement).value)
      internalValue.value = Math.max(props.min, Math.min(newValue, props.max))
      emit('input', internalValue.value)
    }

    const markerPosition = computed((): string => {
      const thumbHalfSize = thumbSize.value / 2
      const value =
        props.min >= 0
          ? (internalValue.value - props.min) / (props.max - props.min)
          : (internalValue.value - props.max) / (props.min - props.max)
      return `calc(${value} * (100% - ${thumbHalfSize}px - ${thumbHalfSize}px) + ${thumbHalfSize}px)`
    })

    const incrementValue = (): void => {
      if (internalValue.value < props.max) {
        internalValue.value += 1
        emit('input', internalValue.value)
      }
    }
    const decrementValue = (): void => {
      if (internalValue.value > props.min) {
        internalValue.value -= 1
        emit('input', internalValue.value)
      }
    }

    watch(
      () => props.value,
      () => {
        internalValue.value = props.value
      },
      { deep: true, immediate: true },
    )

    onMounted(() => {
      if (internalValue.value < props.min) {
        internalValue.value = props.min
      } else if (internalValue.value > props.max) {
        internalValue.value = props.max
      }
    })

    return {
      baseClass,
      internalValue,
      thumbSize,
      markerVisible,
      inputValue,
      markerPosition,
      incrementValue,
      decrementValue,
      ColorEnum,
    }
  },
})
